import { cn } from '@/shared/utils/cn';
import { AlertProps, AlertColor } from '@/shared/types/alert';
import { AlertHeader } from './components/atoms/AlertHeader';
import { AlertFooter } from './components/atoms/AlertFooter';
import { getAlertClassName } from './utils/alert';
import { useAlert } from './hooks/useAlert';
import { ProgressBar } from '../ProgressBar';

/**
 * Alert - Componente de alerta
 *
 * Tokens semánticos de Figma:
 * - Padding: 16px (space_16) para md, reducido para sm
 * - Gap: 16px (space_16) entre secciones
 * - Border radius: control-sm (8px)
 * - Border width: 1px
 */
export const Alert = ({
  open = false,
  color = 'success',
  variant = 'contained',
  className,
  message,
  title,
  description,
  showCloseButton = true,
  onClose,
  onClick,
  onPrimaryClick,
  onSecondaryClick,
  primaryButtonText = 'Aceptar',
  secondaryButtonText = 'Cancelar',
  animation = 'none',
  isLeaving: externalIsLeaving,
  size = 'sm',
  duration,
  progressBarClassName,
  children,
  avatar,
  endAdornment,
  avatarIntentBadge,
  truncateHeaderText,
  headerAddon,
}: AlertProps) => {
  // Validación de props en desarrollo
  if (process.env.NODE_ENV !== 'production') {
    if (!message && !title) {
      // eslint-disable-next-line no-console
      console.warn('Alert: Either "message" or "title" should be provided');
    }
    if (
      duration !== undefined &&
      (typeof duration !== 'number' || !Number.isFinite(duration) || duration < 0)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `Alert: "duration" must be undefined, 0 (no auto-close), or a positive finite number, received: ${String(duration)}`
      );
    }
    if (color && !['success', 'danger', 'warning', 'info', 'neutral', 'brand'].includes(color)) {
      // eslint-disable-next-line no-console
      console.warn(`Alert: Invalid color "${color}", using "success" as fallback`);
    }
    if (variant && !['contained', 'outlined', 'accent', 'minimal'].includes(variant)) {
      // eslint-disable-next-line no-console
      console.warn(`Alert: Invalid variant "${variant}", using "contained" as fallback`);
    }
    if (size && !['sm', 'md'].includes(size)) {
      // eslint-disable-next-line no-console
      console.warn(`Alert: Invalid size "${size}", using "sm" as fallback`);
    }
    if (
      animation &&
      !['none', 'fade', 'slide', 'zoom', 'flip', 'bounce', 'slide-up'].includes(animation)
    ) {
      // eslint-disable-next-line no-console
      console.warn(`Alert: Invalid animation "${animation}", using "none" as fallback`);
    }
  }

  const { isVisible, isLeaving } = useAlert({ open, externalIsLeaving });

  if (!isVisible) {
    return null;
  }

  const isClickable = !!onClick;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    e.stopPropagation();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  // Padding según tamaño - Figma usa 16px (space_16) para md, 12px para sm
  const sizeClasses = {
    sm: 'ui-p-12', // 12px
    md: 'ui-p-16', // 16px (space_16)
  };

  // Mapear colores de AlertColor a ProgressBarColor
  const getProgressBarColor = (
    alertColor: typeof color
  ): 'brand' | 'success' | 'danger' | 'warning' | 'info' | 'neutral' => {
    const colorMap: Record<
      AlertColor,
      'brand' | 'success' | 'danger' | 'warning' | 'info' | 'neutral'
    > = {
      success: 'success',
      danger: 'danger',
      warning: 'warning',
      info: 'info',
      neutral: 'neutral',
      brand: 'brand',
    };
    return colorMap[alertColor] || 'danger';
  };

  // Determinar aria-live según el color (assertive para danger, polite para otros)
  const ariaLive = color === 'danger' ? 'assertive' : 'polite';

  return (
    <div
      role="alert"
      aria-live={ariaLive}
      aria-atomic="true"
      className={cn(
        'ui-imj',
        'ui-imj-alert',
        'ui-relative',
        'ui-overflow-hidden',
        'ui-flex',
        'ui-flex-col',
        getAlertClassName({ color, variant, isLeaving, animation }),
        isClickable &&
          'ui-cursor-pointer ui-transition-all ui-duration-300 ui-ease-in-out hover:ui-brightness-110',
        className
      )}
    >
      <div
        className={cn(
          'ui-relative',
          'ui-flex',
          'ui-w-full',
          'ui-min-w-0',
          'ui-flex-col',
          'ui-gap-6',
          sizeClasses[size]
        )}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={isClickable ? handleClick : undefined}
        onKeyDown={isClickable ? handleKeyDown : undefined}
      >
        <div className="ui-w-full ui-min-w-0 ui-flex-1">
          <AlertHeader
            message={message}
            title={title}
            description={description}
            color={color}
            variant={variant}
            showCloseButton={showCloseButton}
            onClose={onClose}
            onPrimaryClick={onPrimaryClick}
            onSecondaryClick={onSecondaryClick}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            avatar={avatar}
            endAdornment={endAdornment}
            avatarIntentBadge={avatarIntentBadge}
            truncateHeaderText={truncateHeaderText}
            headerAddon={headerAddon}
          />
        </div>
        {children !== null && children !== undefined ? (
          <div className="ui-w-full ui-min-w-0">{children}</div>
        ) : null}
      </div>
      {/* Barra de progreso según diseño Figma - fondo gris claro, fill según color */}
      {/* Altura: 8px (xxs), fondo: bg-fill-tertiary, sin label ni progress */}
      {/* Solo se muestra si hay duración definida y es un número válido */}
      {duration && typeof duration === 'number' && duration > 0 && Number.isFinite(duration) ? (
        <div className={cn('ui-w-full', progressBarClassName)}>
          <ProgressBar
            progress={100}
            size="xxs"
            color={getProgressBarColor(color)}
            showLabel={false}
            showProgress={false}
            withBackground={true}
            countdown={duration / 1000}
            onCountdownComplete={() => {
              if (onClose) {
                // Crear un evento sintético para mantener la firma del callback
                const syntheticEvent = {
                  stopPropagation: () => {},
                  preventDefault: () => {},
                } as React.MouseEvent<HTMLButtonElement>;
                onClose(syntheticEvent);
              }
            }}
            className="ui-w-full"
          />
        </div>
      ) : null}
    </div>
  );
};
